import React, { useEffect, useState, memo } from 'react';
import { View, Text } from '@adobe/react-spectrum';
import { FormField } from '../store/slice';
import {
  useFieldVisibility,
  isFieldVisible,
  updateFieldVisibility
} from './utils/fieldVisibility';
import { TextFieldComponent } from './components/TextFieldComponent';
import { DropdownComponent } from './components/DropdownComponent';
import { DateComponent } from './components/DateComponent';
import { SwitchComponent } from './components/SwitchComponent';
import { RepeatableFieldComponent } from './components/RepeatableFieldComponent';
import { RichTextEditorComponent } from './components/RichTextEditorComponent';
import { DropdownWithRichTextEditor } from './components/DropdownWithRichTextEditor';
import { MultiSelectComponent } from './components/MultiSelectComponent';
import { MultiSelectDropdownComponent } from './components/MultiSelectDropdownComponent';
import { MultiSelectWithRichTextEditor } from './components/MultiSelectWithRichTextEditor';
import LDAPSearchField from '../../shared/LDAPSearch/LDAPSearchField';
import styles from './FormField.module.scss';
import { parseISO, format } from 'date-fns';
import { DatePicker } from '@adobe/react-spectrum';
import { CalendarDate } from '@internationalized/date';
import { DateRangePickerComponent } from './components/DateRangePickerComponent';
import { CustomComponentField } from './components/CustomComponentField';
import { GroupComponentField } from './components/GroupComponentField';
import { MultiSelectApiDropdown } from './components/MultiSelectApiDropdown';
import { CheckboxGroupComponent } from './components/CheckboxGroupComponent';
import { RepeatableGroupComponentField } from './components/RepeatableGroupComponentField';
import { PHASE_FIELDS } from './utils/phaseFields';
import { END_OF_LIFE_MOMENT_TYPE } from '../utils/eolCreatePayload';
import { isEolLaunchRichTextField } from './utils/eolStagesValidation';

interface FormFieldComponentProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  onBlur?: (name: string) => void;
  formValues: Record<string, any>;
  error?: string;
  hasSubmitted?: boolean;
  // Optional section-level invalid override (e.g., Primary Contacts section)
  sectionInvalid?: boolean;
  toolTipMessage?: string;
  disabled?: boolean;
}

/**
 * Unified form field component that renders different field types
 * This component is now significantly simplified by delegating to specialized components
 */
const FormFieldComponent: React.FC<FormFieldComponentProps> = memo(
  ({
    field,
    value,
    onChange,
    onBlur,
    formValues,
    error,
    hasSubmitted = false,
    sectionInvalid,
    disabled: propDisabled
  }) => {
    // Helper function to check if rich text content is truly empty (strips HTML tags)
    const isRichTextContentEmpty = (content: string): boolean => {
      if (!content || content === '' || content.trim() === '') {
        return true;
      }
      // Check if it's just empty HTML tags (like <p><br></p> from Quill)
      const textContent = content.replace(/<[^>]*>/g, '').trim();
      return textContent === '';
    };

    // Helper: determine if a field's value is effectively empty for validation
    const isValueEmpty = (fieldObj: FormField, currentValue: any): boolean => {
      const fieldType = fieldObj.type;
      // Generic empties
      const isEmptyPrimitive =
        currentValue === undefined ||
        currentValue === null ||
        (typeof currentValue === 'string' && currentValue.trim() === '');

      if (
        fieldType === 'multi_select' ||
        fieldType === 'multi_select_api' ||
        fieldType === 'repeatable'
      ) {
        return !Array.isArray(currentValue) || currentValue.length === 0;
      }

      if (fieldType === 'checkbox_group') {
        // For checkbox_group, value is an object like { genAi: true, consumesCredits: false }
        // Check if at least one option is checked (at least one true value)
        if (!currentValue || typeof currentValue !== 'object') return true;
        return !Object.values(currentValue).some(val => val === true);
      }

      if (fieldType === 'multi_select_richtext') {
        if (!currentValue || typeof currentValue !== 'object') return true;
        const hasOptions =
          Array.isArray(currentValue.options) &&
          currentValue.options.length > 0;
        // Check if content is truly empty (strip HTML tags like main form validation)
        const hasContent =
          typeof currentValue.content === 'string' &&
          !isRichTextContentEmpty(currentValue.content);
        // Require both options and content
        return !(hasOptions && hasContent);
      }

      if (fieldType === 'dropdown_richtext') {
        if (!currentValue || typeof currentValue !== 'object') return true;
        const hasOption = Boolean(currentValue.option);
        // Check if content is truly empty (strip HTML tags like main form validation)
        const hasContent =
          typeof currentValue.content === 'string' &&
          !isRichTextContentEmpty(currentValue.content);
        // Require both option and content
        return !(hasOption && hasContent);
      }

      if (fieldType === 'richtext') {
        if (
          currentValue === '' ||
          (typeof currentValue === 'string' && currentValue.trim() === '')
        ) {
          return true;
        }
        // Check if it's just empty HTML tags like main form validation
        if (typeof currentValue === 'string') {
          return isRichTextContentEmpty(currentValue);
        }
        return false;
      }

      // Switch and checkbox must be explicitly true when required
      if (fieldType === 'switch' || fieldType === 'checkbox') {
        return currentValue !== true;
      }

      // Special rule for specific datepickers (marketingDate): N/A is NOT allowed when required
      if (fieldType === 'datepicker' && fieldObj.name === 'marketingDate') {
        // Treat empty or 'N/A' as invalid, everything else (TBD/date/months/quarters) as valid
        if (!currentValue) return true;
        if (typeof currentValue === 'string') {
          return currentValue.trim() === '' || currentValue === 'N/A';
        }
        if (typeof currentValue === 'object') {
          if (currentValue.type === 'special') {
            return currentValue.value === 'N/A'; // 'TBD' is acceptable
          }
          if (currentValue.type === 'date') {
            return !currentValue.date;
          }
          if (currentValue.type === 'months') {
            return (
              !Array.isArray(currentValue.months) ||
              currentValue.months.length === 0
            );
          }
          if (currentValue.type === 'quarters') {
            return (
              !Array.isArray(currentValue.quarters) ||
              currentValue.quarters.length === 0
            );
          }
        }
        // Unknown structure - be conservative
        return false;
      }

      // Default handling (covers text, select, api_dropdown, richtext, datepicker value strings)
      if (isEmptyPrimitive) return true;

      // Object values (e.g., valueFormat objects for dropdowns)
      if (typeof currentValue === 'object') {
        return Object.keys(currentValue).length === 0;
      }

      return false;
    };

    // Helper: determine if a field should be disabled based on its dependencies
    const isFieldDisabled = (fieldObj: FormField): boolean => {
      if (!fieldObj.dependsOn) return false;

      const dependencies = Array.isArray(fieldObj.dependsOn)
        ? fieldObj.dependsOn
        : [fieldObj.dependsOn];

      // Check if any dependency is empty
      return dependencies.some(dep => {
        const depValue = formValues[dep];
        return isValueEmpty(
          { type: 'text', name: dep, required: false } as FormField,
          depValue
        );
      });
    };

    // Helper to check if ANY tier (top-level or phase-level) is L1 or L2
    const hasL1orL2Tier = (): boolean => {
      // Check top-level tier field (for moment types like "Marketing Moment")
      const topLevelTier = formValues?.tier;
      if (topLevelTier === 'L1' || topLevelTier === 'L2') {
        return true;
      }

      // Check phase-level tier fields
      return PHASE_FIELDS.some(phaseName => {
        const phaseData = formValues[phaseName];
        if (phaseData && typeof phaseData === 'object') {
          const tier = phaseData.tier;
          return tier === 'L1' || tier === 'L2';
        }
        return false;
      });
    };

    // Determine if field is conditionally required
    const isFieldRequired = (): boolean => {
      // EOL: strategic alignment is optional at all tiers; field removed from create form
      if (
        field.name === 'audienceStrategyMappings' &&
        formValues?.momentType === END_OF_LIFE_MOMENT_TYPE
      ) {
        return false;
      }
      // Special case: audienceStrategyMappings is only required when L1/L2 tier exists
      if (field.name === 'audienceStrategyMappings') {
        return field.required && hasL1orL2Tier();
      }
      return field.required;
    };

    const isInvalid =
      hasSubmitted && isFieldRequired() && isValueEmpty(field, value);
    const isEolLaunchRichText =
      formValues?.momentType === END_OF_LIFE_MOMENT_TYPE &&
      isEolLaunchRichTextField(field.id || field.name);
    const showEolLaunchRichTextError = isEolLaunchRichText && isInvalid;
    // Visibility state
    const [isVisible, setIsVisible] = useState(() =>
      isFieldVisible(field.name)
    );

    // Track if user has interacted with the dropdown
    const [hasInteracted, setHasInteracted] = useState(false);

    // Listen for visibility change events
    useEffect(() => {
      // Handler for visibility change events
      const handleVisibilityChange = (event: Event) => {
        const customEvent = event as CustomEvent<{
          fieldName: string;
          isVisible: boolean;
        }>;
        if (customEvent.detail.fieldName === field.name) {
          setIsVisible(customEvent.detail.isVisible);
        }
      };

      // Listen for visibility changes
      window.addEventListener(
        'field-visibility-change',
        handleVisibilityChange
      );

      // Cleanup on unmount
      return () => {
        window.removeEventListener(
          'field-visibility-change',
          handleVisibilityChange
        );
      };
    }, [field.name]);

    // Clear this field's value if its dependency becomes empty
    useEffect(() => {
      if (!field.dependsOn) return;
      const dependencies = Array.isArray(field.dependsOn)
        ? field.dependsOn
        : [field.dependsOn];
      const isEmpty = (v: any) =>
        v === undefined ||
        v === null ||
        (Array.isArray(v) && v.length === 0) ||
        (typeof v === 'string' && v.trim() === '');
      const missingDependency = dependencies.some(dep =>
        isEmpty(formValues[dep])
      );
      if (!missingDependency) return;
      // Clear based on field type
      if (
        (field.type === 'multi_select' ||
          field.type === 'multi_select_api' ||
          field.type === 'multi_select_richtext') &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        onChange([]);
      } else if (field.type === 'checkbox_group' && value) {
        // Clear checkbox_group by setting all options to false
        const clearedValue: Record<string, boolean> = {};
        if (field.options) {
          field.options.forEach(opt => {
            clearedValue[opt.value] = false;
          });
        }
        onChange(clearedValue);
      } else if (
        (field.type === 'select' ||
          field.type === 'api_dropdown' ||
          field.type === 'text' ||
          field.type === 'dropdown_richtext') &&
        value
      ) {
        onChange(undefined);
      }
    }, [field.dependsOn, formValues, field.type, value, onChange]);

    // Initialize any dependent visibility based on switch state
    useEffect(() => {
      // If this is a switch field with dependents and actions
      if (field.type === 'switch' && field.dependents && field.actions) {
        // Apply initial actions based on switch value
        const isSelected = Boolean(value);
        const actionIndex = isSelected ? 0 : 1;
        const action = field.actions[actionIndex];

        // Apply the action to all dependent fields
        field.dependents.forEach(dependentField => {
          updateFieldVisibility(dependentField, action === 'show');
        });
      }
    }, [field.type, field.dependents, field.actions, value]);

    // Special case for mirrorName field
    useEffect(() => {
      // If this field is a dependent of the launchEnabled switch
      if (field.name === 'mirrorName') {
        const launchEnabled = formValues && formValues['launchEnabled'];
        if (launchEnabled === false || launchEnabled === undefined) {
          updateFieldVisibility(field.name, false);
        } else {
          updateFieldVisibility(field.name, true);
        }
      }
    }, [field.name, formValues]);

    // Determine if the field should be visually hidden. We keep it mounted so
    // internal effects (e.g., auto-populate) still run and values flow into state/payload.
    const isEolStrategicAlignment =
      field.name === 'audienceStrategyMappings' &&
      formValues?.momentType === END_OF_LIFE_MOMENT_TYPE;
    const isHidden =
      field.showField === false || !isVisible || isEolStrategicAlignment;

    // Special case for spacer fields
    if (field.type === 'spacer') {
      return (
        <View
          UNSAFE_className={`${styles.emptyField} ${
            isHidden ? styles.hidden : ''
          }`}
        ></View>
      );
    }

    // LDAP field type
    if (field.type === 'ldap') {
      // Check if this is one of the 3 manager LDAP fields
      const managerLdapFields = [
        'productManagers',
        'productMarketingManagers',
        'programManagers'
      ];
      const isManagerLdapField = managerLdapFields.includes(field.name);

      let isLdapEmpty = !Array.isArray(value) || value.length === 0;

      // If this is a manager LDAP field, check if ANY of the 3 manager fields has values
      if (isManagerLdapField) {
        const hasAnyManagerSelected = managerLdapFields.some(fieldName => {
          const fieldValue = formValues[fieldName];
          return Array.isArray(fieldValue) && fieldValue.length > 0;
        });

        // If any manager field has values, don't show error for any of them
        if (hasAnyManagerSelected) {
          isLdapEmpty = false;
        }
      }

      const showError =
        (hasSubmitted && field.required && isLdapEmpty) ||
        Boolean(sectionInvalid);

      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${showError ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <div className={styles.ldapFieldWrapper}>
            <label className={styles.fieldLabel}>
              {field.label}
              {field.required && !(field as any)?.sectionConfig?.required && (
                <span className={styles.required}> *</span>
              )}
            </label>
            <div className={styles.ldapSearchContainer}>
              <LDAPSearchField
                label=""
                value={value}
                onChange={onChange}
                isRequired={field.required}
                isInvalid={showError}
                cssClasses={{
                  ldapFieldContainer: styles.ldapSearchContainer,
                  ldapTextField: styles.ldapTextField,
                  selectedTag: styles.selectedTag,
                  tagRemoveButton: styles.tagRemoveButton,
                  ldapDropdown: styles.ldapDropdown
                }}
              />
            </div>
          </div>
        </View>
      );
    }

    // Dropdown field types (select, api_dropdown)
    if (field.type === 'select' || field.type === 'api_dropdown') {
      // Handle both string and array dependencies
      const dependencies = Array.isArray(field.dependsOn)
        ? field.dependsOn
        : [field.dependsOn];

      // Check if any dependency is missing
      const missingDependency = field.dependsOn
        ? dependencies.some(dep => !formValues[dep])
        : false;

      const showDependencyMessage =
        field.dependsOn && missingDependency && hasInteracted;

      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <DropdownComponent
            field={field}
            value={value}
            onChange={onChange}
            formValues={formValues}
            onFocus={() => setHasInteracted(true)}
            validationState={isInvalid ? 'invalid' : 'valid'}
            disabled={field.disabled || isFieldDisabled(field)}
          />
          {showDependencyMessage && (
            <Text
              UNSAFE_style={{
                marginTop: '4px',
                textTransform: 'lowercase',
                color: 'red'
              }}
            >
              Please Select{' '}
              {Array.isArray(field.dependsOn)
                ? field.dependsOn.join(' and ')
                : field.dependsOn}{' '}
              First
            </Text>
          )}
        </View>
      );
    }

    // Multi-select dropdown field
    if (field.type === 'multi_select') {
      const dependsOnValue = field.dependsOn
        ? formValues[field.dependsOn]
        : null;
      const showDependencyMessage =
        field.dependsOn && !dependsOnValue && hasInteracted;

      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <MultiSelectComponent
            field={field}
            value={Array.isArray(value) ? value : []}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            formValues={formValues}
            onFocus={() => setHasInteracted(true)}
            disabled={isFieldDisabled(field)}
          />
          {showDependencyMessage && (
            <Text
              UNSAFE_style={{
                fontSize: '12px',
                marginTop: '4px',
                fontStyle: 'italic'
              }}
            >
              Please Select {field.dependsOn} First
            </Text>
          )}
        </View>
      );
    }

    // Multi-select dropdown field with API
    if (field.type === 'multi_select_api') {
      const disabled = isFieldDisabled(field);
      const dependsOnValue = field.dependsOn
        ? formValues[field.dependsOn]
        : null;
      const showDependencyMessage =
        field.dependsOn && !dependsOnValue && hasInteracted;

      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid && !disabled ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <MultiSelectApiDropdown
            field={field}
            value={Array.isArray(value) ? value : []}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            formValues={formValues}
            onFocus={() => setHasInteracted(true)}
            isInvalid={isInvalid && !disabled}
            disabled={disabled}
          />
          {showDependencyMessage && (
            <Text
              UNSAFE_style={{
                marginTop: '4px',
                color: '#d31510',
                fontSize: '12px',
                textTransform: 'none'
              }}
            >
              Please select {field.dependsOn} first
            </Text>
          )}
        </View>
      );
    }

    // Text field (text)
    if (field.type === 'text') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <TextFieldComponent
            field={field}
            value={value}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            onBlur={onBlur}
            validationState={isInvalid ? 'invalid' : 'valid'}
          />
        </View>
      );
    }

    //Custom Datepicker field
    if (field.type === 'datepicker') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <DateComponent
            field={field}
            value={value}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            validationState={isInvalid ? 'invalid' : 'valid'}
            disableNA={field.disableNA}
          />
        </View>
      );
    }

    // DateRangePicker field
    if (field.type === 'daterangepicker') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <DateRangePickerComponent
            field={field}
            value={value}
            onChange={newValue => {
              onChange(newValue);
            }}
            disablePastDate={false}
            enforceEndDateAfterStart={true}
            validationState={isInvalid ? 'invalid' : 'valid'}
          />
        </View>
      );
    }

    // Checkbox group field (multiple checkboxes with dependencies)
    if (field.type === 'checkbox_group') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <CheckboxGroupComponent
            field={field}
            value={value || {}}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            validationState={isInvalid ? 'invalid' : 'valid'}
          />
        </View>
      );
    }

    // Switch and checkbox fields
    if (field.type === 'switch' || field.type === 'checkbox') {
      // If propDisabled is explicitly provided (not undefined), use it
      // Otherwise, fall back to field-level disabled checks
      const finalDisabled =
        propDisabled !== undefined
          ? propDisabled
          : field.disabled || isFieldDisabled(field);

      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <SwitchComponent
            field={field}
            value={value}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            disabled={finalDisabled}
          />
        </View>
      );
    }

    // After handling other field types, add support for rich text editor
    if (field.type === 'richtext') {
      return (
        <div
          className={`${styles.formField} ${isHidden ? styles.hidden : ''} ${
            isInvalid ? styles.error : ''
          }`}
          data-field-name={field.name}
          data-field-id={field.id}
          data-validation-error={
            showEolLaunchRichTextError ? 'true' : undefined
          }
        >
          <RichTextEditorComponent
            field={field}
            value={value || ''}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            isInvalid={isInvalid}
            minHeight="100px"
          />
        </div>
      );
    }

    // After handling other field types, add support for dropdown with rich text
    if (field.type === 'dropdown_richtext') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <DropdownWithRichTextEditor
            field={field}
            value={value || { option: '', content: '' }}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            validationState={isInvalid ? 'invalid' : 'valid'}
          />
        </View>
      );
    }

    // After handling other field types, add support for repeatable fields
    if (field.type === 'repeatable') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <RepeatableFieldComponent
            field={field}
            value={value || []}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
          />
        </View>
      );
    }

    // Multi-select dropdown with rich text editor

    if (field.type === 'multi_select_richtext') {
      const hasNoSelection =
        !value || !value.options || value.options.length === 0;
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <MultiSelectWithRichTextEditor
            field={field}
            value={value || { options: [], content: '' }}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            formValues={formValues}
            isInvalid={isInvalid}
          />
        </View>
      );
    }

    // Custom component field type
    if (field.type === 'custom_component') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <CustomComponentField
            field={field}
            value={value}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            formValues={formValues}
            hasSubmitted={hasSubmitted}
          />
        </View>
      );
    }

    if (field.type === 'repeatable_group_component') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <RepeatableGroupComponentField
            field={field}
            value={value}
            onChange={(fieldName, newValue) => {
              onChange(newValue);
            }}
            hasSubmitted={hasSubmitted}
            isInvalid={isInvalid}
          />
        </View>
      );
    }

    // Group component field type
    if (field.type === 'group_component') {
      return (
        <View
          UNSAFE_className={`${styles.formField} ${
            isHidden ? styles.hidden : ''
          } ${isInvalid ? styles.error : ''}`}
          data-field-name={field.name}
        >
          <GroupComponentField
            field={field}
            value={value}
            onChange={(fieldName, newValue) => {
              // Pass the complete state up directly
              onChange(newValue);
            }}
            formValues={formValues}
          />
        </View>
      );
    }

    // Default case
    return (
      <View UNSAFE_className={styles.formField}>
        <Text>Unsupported field type: {field.type}</Text>
      </View>
    );
  },
  // Add custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    // Always re-render if the field's own value changes
    if (prevProps.value !== nextProps.value) {
      return false; // Re-render
    }

    // Re-render when disabled prop changes
    if (prevProps.disabled !== nextProps.disabled) {
      return false; // Re-render
    }

    // Re-render when submission state changes (to show/hide validation)
    if (prevProps.hasSubmitted !== nextProps.hasSubmitted) {
      return false;
    }

    // Re-render when section-level invalid flag changes (e.g., Primary Contacts)
    if (prevProps.sectionInvalid !== nextProps.sectionInvalid) {
      return false;
    }

    if (prevProps.error !== nextProps.error) {
      return false;
    }

    // Re-render when field config changes (e.g. inputPrepend on moment type switch)
    if (
      prevProps.field.inputPrepend !== nextProps.field.inputPrepend ||
      prevProps.field.inputPrependLabel !== nextProps.field.inputPrependLabel ||
      prevProps.field.label !== nextProps.field.label ||
      prevProps.field.type !== nextProps.field.type
    ) {
      return false;
    }

    // Special case: showOnRoadmap needs to re-render when marketingDate changes
    // (since its disabled state depends on marketingDate vs effectiveDate comparison)
    if (
      prevProps.field.name === 'showOnRoadmap' &&
      prevProps.formValues &&
      nextProps.formValues
    ) {
      if (
        prevProps.formValues.marketingDate !==
        nextProps.formValues.marketingDate
      ) {
        return false; // Re-render
      }
    }

    // Special case: releaseDateInfo (custom component) must re-render when marketingDate changes
    // so it can re-evaluate date equality and auto-reset showOnRoadmap when dates become equal.
    if (
      prevProps.field.name === 'releaseDateInfo' &&
      prevProps.field.type === 'custom_component' &&
      prevProps.formValues &&
      nextProps.formValues
    ) {
      if (
        prevProps.formValues.marketingDate !==
        nextProps.formValues.marketingDate
      ) {
        return false; // Re-render
      }
    }

    // Special case: audienceStrategyMappings needs to re-render when any tier changes (top-level or phase-level)
    if (
      prevProps.field.name === 'audienceStrategyMappings' &&
      prevProps.formValues &&
      nextProps.formValues
    ) {
      // Check top-level tier change
      if (prevProps.formValues.tier !== nextProps.formValues.tier) {
        return false; // Re-render
      }

      // Check phase-level tier changes
      const tierChanged = PHASE_FIELDS.some(phaseName => {
        const prevTier = prevProps.formValues[phaseName]?.tier;
        const nextTier = nextProps.formValues[phaseName]?.tier;
        return prevTier !== nextTier;
      });

      if (tierChanged) {
        return false; // Re-render
      }
    }

    // For fields with dependencies, re-render if the dependency changes
    if (
      prevProps.field.dependsOn &&
      prevProps.formValues &&
      nextProps.formValues
    ) {
      // Handle both string and array dependencies
      const dependencies = Array.isArray(prevProps.field.dependsOn)
        ? prevProps.field.dependsOn
        : [prevProps.field.dependsOn];

      // Check if any dependency has changed
      const hasChangedDependency = dependencies.some(dep => {
        const prevValue = prevProps.formValues[dep];
        const nextValue = nextProps.formValues[dep];
        return prevValue !== nextValue;
      });

      if (hasChangedDependency) {
        return false; // Re-render
      }
    }

    // For API dropdown fields, only re-render if specific dependencies change
    if (
      (prevProps.field.type === 'api_dropdown' ||
        prevProps.field.type === 'multi_select_api') &&
      prevProps.field.api &&
      prevProps.formValues &&
      nextProps.formValues
    ) {
      // Only consider relevant form values for this field
      const dependencies = Array.isArray(prevProps.field.dependsOn)
        ? prevProps.field.dependsOn
        : prevProps.field.dependsOn
        ? [prevProps.field.dependsOn]
        : [];

      const relevantPrev = {};
      const relevantNext = {};

      dependencies.forEach(dep => {
        relevantPrev[dep] = prevProps.formValues[dep];
        relevantNext[dep] = nextProps.formValues[dep];
      });

      // Compare only the relevant dependencies
      if (JSON.stringify(relevantPrev) !== JSON.stringify(relevantNext)) {
        return false; // Re-render
      }
    }

    // Otherwise, prevent re-rendering (return true from memo comparison)
    return true;
  }
);

export { FormFieldComponent };
